import React, { useMemo, useState } from 'react';

/**
 * Orden de columnas para las tablas de GPVoice.
 *
 * Ordena EN EL CLIENTE, así que sólo es correcto en tablas que ya traen todas
 * sus filas (agentes, colas, cumplimiento). Una tabla paginada en el servidor
 * necesita que el orden viaje al backend: ordenar sólo la página visible daría
 * un "top 10" que en realidad es "los 10 mejores de los 50 que alcanzaste a
 * ver", y el usuario no tendría forma de notarlo. Para esos casos ver el
 * parámetro `sort_by` del listado de llamadas.
 */

/** Comparador estable: números como números, texto con locale, nulos al final. */
function comparar(a, b) {
  const vacioA = a === null || a === undefined || a === '';
  const vacioB = b === null || b === undefined || b === '';
  // Los vacíos van SIEMPRE al final, en las dos direcciones: un "-" arriba de
  // la lista al ordenar descendente no le dice nada a nadie.
  if (vacioA && vacioB) return 0;
  if (vacioA) return 1;
  if (vacioB) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);

  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;

  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

/**
 * @param {Array} filas
 * @param {Object<string, (fila:any)=>any>} columnas  clave → cómo sacar su valor
 * @param {string|null} inicial  columna con la que arranca (o null = sin orden)
 * @param {'asc'|'desc'} dirInicial
 */
export function useOrdenTabla(filas, columnas, inicial = null, dirInicial = 'desc') {
  const [clave, setClave] = useState(inicial);
  const [dir, setDir] = useState(dirInicial);

  const ordenadas = useMemo(() => {
    if (!clave || !columnas[clave]) return filas;
    const extraer = columnas[clave];
    // Copia: ordenar in-place mutaría el array del padre y React no lo vería.
    const copia = [...(filas || [])];
    copia.sort((x, y) => {
      const r = comparar(extraer(x), extraer(y));
      return dir === 'asc' ? r : -r;
    });
    return copia;
  }, [filas, columnas, clave, dir]);

  /** Un clic ordena descendente; el siguiente en la misma columna invierte. */
  const alternar = (nueva) => {
    if (nueva === clave) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setClave(nueva);
      setDir('desc');
    }
  };

  return { filas: ordenadas, clave, dir, alternar };
}

/** Encabezado que ordena al hacer clic. */
export function ThOrdenable({ columna, orden, children, title }) {
  const activa = orden.clave === columna;
  return (
    <th
      className="th-ordenable"
      onClick={() => orden.alternar(columna)}
      title={title || 'Ordenar por esta columna'}
      aria-sort={activa ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      <span className={activa ? 'flecha-orden' : 'flecha-inactiva'}>
        {activa ? (orden.dir === 'asc' ? '▲' : '▼') : '▼'}
      </span>
    </th>
  );
}
